/**
 * Frontend mirror of backend `agent/skills.py` permission maps. Used as
 * defense-in-depth for the cross-role center-panel S3 leak fix
 * (backlog 0bb94516, fixed 2026-05-08): the backend already strips gated
 * components from GET /chat/center_panel and GET /chat/session_context,
 * but if it ever slips, the frontend refuses to render the component
 * rather than leaking content to a user who shouldn't see it.
 *
 * IMPORTANT: keep in sync with backend `agent/skills.py`. If you add a new
 * skill, group permission, or center-panel component there, mirror it here.
 * The two source-of-truth tables (DEFAULT_SKILL_PERMISSIONS,
 * COMPONENT_REQUIRED_SKILL) live there; this file is the JS shadow.
 */

// Group → skill names. Mirrors DEFAULT_SKILL_PERMISSIONS in agent/skills.py.
// '*' means all skills. Keep entries in sync.
const GROUP_SKILLS = {
  'Super Admins': ['*'],
  'Org Admins': [
    'analysis', 'topic_curation', 'data_enrichment',
    'report', 'presentation', 'planning', 'pipeline_hil',
  ],
  'Customer Admins': [
    'analysis', 'topic_curation', 'data_enrichment',
    'report', 'presentation', 'planning', 'pipeline_hil',
  ],
  'Admins': [
    'analysis', 'topic_curation', 'data_enrichment',
    'report', 'presentation', 'planning', 'pipeline_hil',
  ],
  'Editors': [
    'analysis', 'topic_curation', 'data_enrichment',
    'report', 'presentation', 'planning', 'pipeline_hil',
  ],
  Contributors: ['analysis', 'presentation', 'planning'],
  Viewers: ['presentation'],
  'Authenticated Users': [],
};

// Component → skill required to view it. Mirrors COMPONENT_REQUIRED_SKILL
// in agent/skills.py. Components NOT in this map are not gated at the
// component level (other gates — session-required-skill, owner check —
// still apply at the backend).
export const COMPONENT_REQUIRED_SKILL = {
  TopicDetailPanel: 'topic_curation',
  GeoMapPanel: 'topic_curation',
  TransactionAnalyzerPanel: 'analysis',
  SchemaAdaptiveEditor: 'pipeline_hil',
  DocumentSplitEditor: 'pipeline_hil',
  BulkEditPreviewDialog: 'pipeline_hil',
  CheckImageReviewPanel: 'pipeline_hil',
};

/**
 * Resolve the union of skills the user can use, given their UserProfile.
 * Returns a Set of skill names. The literal string '*' indicates wildcard
 * (Super Admin). isSuperAdmin alone is treated as wildcard for safety —
 * even if their group list is missing.
 */
export function getPermittedSkills(userProfile) {
  if (!userProfile) return new Set();
  if (userProfile.isSuperAdmin) return new Set(['*']);
  const groups = [
    ...(userProfile.orgGroups || []),
    ...(userProfile.caseGroups || []),
  ];
  const allowed = new Set();
  for (const group of groups) {
    const skills = GROUP_SKILLS[group] || [];
    if (skills.includes('*')) return new Set(['*']);
    skills.forEach(s => allowed.add(s));
  }
  return allowed;
}

/**
 * True if the user can view a center-panel component of the given type.
 * Components not in COMPONENT_REQUIRED_SKILL pass through (no gate at
 * the component level).
 */
export function userCanSeeComponent(permittedSkills, componentType) {
  if (!componentType) return true;
  const required = COMPONENT_REQUIRED_SKILL[componentType];
  if (!required) return true;
  if (!permittedSkills) return false;
  if (permittedSkills.has('*')) return true;
  return permittedSkills.has(required);
}
